import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import VideoContainer from 'components/VideoContainer';
import WatchDetail from 'components/WatchDetail';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import { isStaticRender } from 'helpers/ContentHelper';
import { helperGetHomeUrl } from 'helpers/RouteHelper';
import { noop } from 'helpers/GlobalHelper';
import {
  playerPlay,
  playerPause,
  playerHideControls,
  playerSetFullscreen,
  setStillWatchingShowTimeout,
  setStillWatchingHideTimeout,
  stillWatchingHideCb,
  stillWatchingShowCb,
  VOD,
} from 'helpers/PlayerHelper';
import { fetchVideo, fetchVideoPanel, clearVideo } from './VideoModalActions';
import { getVideoContainerProps, getWatchDetailProps } from './VideoModalHelpers';
import ActionButtons from './components/ActionButtons';
import watchDetailTheme from './WatchDetail.module.scss';
import videoContainerTheme from './VideoContainer.module.scss';
import Style from './VideoModal.module.scss';

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    enableStillWatchingTimeout: true,
  },
  [SITE_PLATFORM_WEB]: {
    enableStillWatchingTimeout: true,
  },
  [SITE_PLATFORM_TABLET]: {
    enableStillWatchingTimeout: false,
  },
  [SITE_PLATFORM_MOBILE]: {
    enableStillWatchingTimeout: false,
  },
};

class VideoModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      adPlaying: false,
      firstFrame: false,
      isAuthError: null,
      isFullscreen: false,
      playerLoading: true,
      showControls: null,
      showStillWatching: false,
      stickyControls: false,
      stillWatchingShowTimeout: null,
      stillWatchingHideTimeout: null,
      // isVideoOverlayPresent is used in stillWatchingHideCb and stillWatchingShowCb
      // eslint-disable-next-line react/no-unused-state
      isVideoOverlayPresent: false,
    };
    this.stillWatchingHideCb = stillWatchingHideCb.bind(this);
    this.stillWatchingShowCb = stillWatchingShowCb.bind(this);
  }

  componentDidMount() {
    const { videoId, accessToken, deviceId, fetchVideoPanel } = this.props;
    if (fetchVideoPanel && !isStaticRender()) {
      fetchVideoPanel(videoId, accessToken, deviceId);
    }
  }

  componentDidUpdate(prevProps) {
    const { accessToken, authSuccess, deviceId, fetchVideo, panelVideo } = this.props;
    const {
      accessToken: prevAccessToken,
      authSuccess: prevAuthSuccess,
      panelVideo: prevPanelVideo,
    } = prevProps;
    const playerScreenUrl = _.get(panelVideo, 'playerScreenUrl');
    const prevPlayerScreenUrl = _.get(prevPanelVideo, 'playerScreenUrl');
    const playerScreenUrlChanged = playerScreenUrl !== prevPlayerScreenUrl;
    const authSuccessChanged = authSuccess !== prevAuthSuccess;
    const accessTokenChanged = accessToken !== prevAccessToken;
    const shouldFetchVideo =
      playerScreenUrl &&
      authSuccess &&
      accessToken &&
      (authSuccessChanged || playerScreenUrlChanged || accessTokenChanged);

    if (shouldFetchVideo) {
      fetchVideo(playerScreenUrl, accessToken, deviceId);
    }
  }

  componentWillUnmount() {
    const { clearVideo } = this.props;
    clearVideo();
  }

  setStickyControls = sticky => {
    this.setState({ stickyControls: sticky });
  };

  callbackShow() {
    const { breakpoint, account } = this.props;
    const { firstFrame, stillWatchingShowTimeout, stillWatchingHideTimeout } = this.state;
    const profileId = _.get(account, 'profileId');
    const enableStillWatchingTimeout = BREAKPOINTS[breakpoint];
    clearTimeout(stillWatchingShowTimeout);

    if (enableStillWatchingTimeout && firstFrame) {
      playerSetFullscreen(false);
      playerPause();
      playerHideControls();
      this.stillWatchingShowCb({ profileId });
      const newStillWatchingHideTimeout = setStillWatchingHideTimeout({
        cb: () => {
          playerPlay();
          this.stillWatchingHideCb();
          clearTimeout(stillWatchingHideTimeout);
          window.location = helperGetHomeUrl();
        },
      });
      this.setState({ stillWatchingHideTimeout: newStillWatchingHideTimeout });
    }
  }

  resetStillWatchingTimeout() {
    const { breakpoint } = this.props;
    const { firstFrame, stillWatchingShowTimeout } = this.state;
    clearTimeout(stillWatchingShowTimeout);
    const durationSeconds = _.get(window.ExternalConfig, 'stillWatching.vodLimitSeconds');
    const duration = durationSeconds ? durationSeconds * 1000 : null;
    const enableStillWatchingTimeout = BREAKPOINTS[breakpoint];
    if (enableStillWatchingTimeout && firstFrame) {
      const newStillWatchingShowTimeout = setStillWatchingShowTimeout({
        cb: this.callbackShow,
        duration,
      });
      this.setState({ stillWatchingShowTimeout: newStillWatchingShowTimeout });
    }
  }

  renderPlayer({ video }) {
    const {
      accessToken,
      account,
      adblock,
      anonymousId,
      anonymousLogin,
      authSuccess,
      blackoutConfig,
      blackoutDefinitions,
      breakpoint,
      deviceId,
      isStatic,
      openSignIn,
      openSignUp,
      profileId,
      provider,
      releaseUrl,
      renderAuthContainer,
      setAccountCloseIgnoreRedirect,
      setAuthorizationToken,
      setIsPlayerLoaded,
      setInterval,
      setIsAuthenticated,
      setMVPDToAppState,
      setOverlay,
      setProvider,
    } = this.props;
    const {
      adPlaying,
      playerLoading,
      showControls,
      showStillWatching,
      stickyControls,
    } = this.state;
    const videoContainerProps = getVideoContainerProps(video);
    const containerClass = null;
    const continuous = false;
    const mediaInfo = video;
    const restart = false;
    const restartPlayerScreenUrl = null;
    const requestSignIn = false;
    const showResumeCTA = false;
    const shouldShowControls = showControls || stickyControls;
    const durationSeconds = _.get(window.ExternalConfig, 'stillWatching.vodLimitSeconds');
    const duration = durationSeconds ? durationSeconds * 1000 : null;

    return (
      <div className={Style.videoContainerWrapper}>
        <VideoContainer
          {...videoContainerProps}
          account={account}
          authSuccess={authSuccess}
          adblock={adblock}
          anonymousId={anonymousId}
          anonymousLogin={anonymousLogin}
          backfade={shouldShowControls && !adPlaying}
          blackoutConfig={blackoutConfig}
          blackoutDefinitions={blackoutDefinitions}
          breakpoint={breakpoint}
          containerClass={containerClass}
          continuous={continuous}
          deviceId={deviceId}
          isAltHeader={false}
          isStatic={isStatic}
          mediaInfo={mediaInfo}
          mode={VOD}
          onStillWatchingClose={() => {
            this.resetStillWatchingTimeout();
            this.setState({ showStillWatching: false });
            playerPlay();
          }}
          openSignIn={openSignIn}
          openSignUp={openSignUp}
          renderAuthContainer={renderAuthContainer}
          playerConfig={{
            mediaInfo,
            percentWatched: 0,
            continuous,
            restart,
            restartPlayerScreenUrl,
            onShowControls: e => {
              const { showControls: oldShowControls, isAuthError: lclIsAuthError } = this.state;
              if (oldShowControls !== e && !lclIsAuthError) {
                this.setState({ showControls: e });
              }
            },
            onAdPodStart: () => {
              this.setState({
                adPlaying: true,
                playerLoading: false,
              });
            },
            onAdPodComplete: () => {
              this.setState({ adPlaying: false });
            },
            onRemovePlayer: () => {
              const { isFullscreen: fullScreen } = this.state;
              if (fullScreen) playerSetFullscreen(false);
            },
            onPlayerLoadComplete: () => {
              const { isAuthError: lclIsAuthError } = this.state;
              this.setState({ playerLoading: false });
              const player = _.get(window, 'player');
              if (lclIsAuthError && player && player.remove) player.remove();
            },
            onFirstFrame: async () => {
              const {
                anonymousAccount: currentAnonymousAccount,
                account: currentAccount,
                anonymousLogin: currentAnonymousLogin,
                anonymousId: currentAnonymousId,
              } = this.props;
              const loginAnonymousUser =
                !currentAccount &&
                !currentAnonymousAccount &&
                currentAnonymousId &&
                currentAnonymousLogin;
              if (loginAnonymousUser) {
                await currentAnonymousLogin({ deviceId: currentAnonymousId });
              }
              const newStillWatchingShowTimeout = setStillWatchingShowTimeout({
                cb: this.callbackShow,
                duration,
              });
              this.setState({
                stillWatchingShowTimeout: newStillWatchingShowTimeout,
                playerLoading: false,
                firstFrame: true,
              });
            },
            onAdLoadStart: () => {},
            onAdLoadComplete: () => {},
            onMediaStart: () => {},
            onMediaPlaying: () => {},
            onContentStart: () => {},
            onContentPlaying: () => {},
            onContentComplete: () => {},
            onMediaComplete: () => {
              const { toggleVideoModal } = this.props;
              toggleVideoModal({});

              this.setState({ firstFrame: false });
            },
            onFullScreen: e => {
              const { fullscreen } = e;
              this.setState({ isFullscreen: fullscreen });
            },
            onUserInteraction: () => {
              const { stillWatchingShowTimeout } = this.state;
              clearTimeout(stillWatchingShowTimeout);
              const newStillWatchingShowTimeout = setStillWatchingShowTimeout({
                cb: this.callbackShow,
                duration,
              });
              this.setState({ stillWatchingShowTimeout: newStillWatchingShowTimeout });
            },
            onChangeContinuous: () => {},
          }}
          playerLoading={playerLoading}
          profileId={profileId}
          profileToken={accessToken}
          provider={provider}
          releaseURL={releaseUrl}
          restart={restart}
          requestSignIn={requestSignIn}
          setAccountCloseIgnoreRedirect={setAccountCloseIgnoreRedirect}
          setAuthError={value => this.setState({ isAuthError: value })}
          setAuthorizationToken={setAuthorizationToken}
          setInterval={setInterval}
          setIsAuthenticated={setIsAuthenticated}
          setIsPlayerLoaded={setIsPlayerLoaded}
          setMVPDToAppState={setMVPDToAppState}
          setOverlay={setOverlay}
          setProvider={setProvider}
          setStickyControls={this.setStickyControls}
          showResumeCTA={showResumeCTA}
          showEndcards={this.showEndCards}
          showControls={shouldShowControls}
          showStillWatching={showStillWatching}
          theme={videoContainerTheme}
        />
      </div>
    );
  }

  render() {
    const { toggleVideoModal, panelVideo, video, videoData } = this.props;
    return (
      <div className={Style.container}>
        <div className={Style.overlay} onClick={toggleVideoModal} />
        <div className={Style.innerContainer}>
          {this.renderPlayer({ video: video || panelVideo })}
          <WatchDetail {...getWatchDetailProps(videoData)} isModal theme={watchDetailTheme} />
          <ActionButtons videoData={videoData} />
        </div>
      </div>
    );
  }
}

function mapStateToProps({ videoModal }) {
  return {
    panelVideo: videoModal.panelVideo,
    releaseUrl: videoModal.releaseUrl,
    video: videoModal.video,
  };
}

export default connect(mapStateToProps, {
  clearVideo,
  fetchVideo,
  fetchVideoPanel,
})(VideoModal);

VideoModal.defaultProps = {
  accessToken: null,
  account: null,
  adblock: false,
  anonymousAccount: null,
  anonymousId: null,
  anonymousLogin: noop,
  authSuccess: false,
  blackoutConfig: {},
  blackoutDefinitions: {},
  deviceId: null,
  openSignIn: noop,
  openSignUp: noop,
  panelVideo: null,
  profileId: null,
  provider: {},
  releaseUrl: null,
  renderAuthContainer: noop,
  setAccountCloseIgnoreRedirect: noop,
  setIsAuthenticated: noop,
  setIsPlayerLoaded: noop,
  setMVPDToAppState: noop,
  setOverlay: noop,
  setProvider: noop,
  video: null,
  videoData: null,
};

VideoModal.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  adblock: PropTypes.bool,
  anonymousAccount: PropTypes.shape({}),
  anonymousId: PropTypes.string,
  anonymousLogin: PropTypes.func,
  authSuccess: PropTypes.bool,
  blackoutConfig: PropTypes.shape({}),
  blackoutDefinitions: PropTypes.shape({}),
  breakpoint: PropTypes.string.isRequired,
  clearVideo: PropTypes.func.isRequired,
  deviceId: PropTypes.string,
  fetchVideo: PropTypes.func.isRequired,
  fetchVideoPanel: PropTypes.func.isRequired,
  isStatic: PropTypes.bool.isRequired,
  openSignIn: PropTypes.func,
  openSignUp: PropTypes.func,
  panelVideo: PropTypes.shape({}),
  profileId: PropTypes.string,
  provider: PropTypes.shape({}),
  releaseUrl: PropTypes.string,
  renderAuthContainer: PropTypes.func,
  setAccountCloseIgnoreRedirect: PropTypes.func,
  setAuthorizationToken: PropTypes.func.isRequired,
  setInterval: PropTypes.func.isRequired,
  setIsAuthenticated: PropTypes.func,
  setIsPlayerLoaded: PropTypes.func,
  setMVPDToAppState: PropTypes.func,
  setOverlay: PropTypes.func,
  setProvider: PropTypes.func,
  toggleVideoModal: PropTypes.func.isRequired,
  video: PropTypes.shape({}),
  videoData: PropTypes.shape({}),
  videoId: PropTypes.string.isRequired,
};
